import { Box, Button, Center, Flex, HStack, Input, Spinner, Stack, Text } from '@chakra-ui/react';
import { useDebounce } from 'use-debounce';
import { trpcTenant } from '@duke-hq/app-provider-web/tenant/trpcTenant';
import { Trans } from '@lingui/react/macro';
import { ChevronLeftIcon, ChevronRightIcon } from 'lucide-react';
import { ChangeEvent, useState, useMemo, useEffect } from 'react';
import { useSnapshot } from 'valtio';
import { internalEnrolmentState } from '@duke-hq/app-provider-web/tenant/tenantState/InternalEnrolmentState';
import {
  FormContentContainer,
  FormFooterContainer,
  FormMainContainer,
} from '@duke-hq/ui/components/Modals/FormModalLayouts';
import {
  CourseKindLabels,
  courseSection,
} from '@duke-hq/app-provider-web/tenant/Courses/constants';
import { ConsultantBanner } from '@duke-hq/app-provider-web/tenant/components/InternalEnrolmentForms/ConsultantBanner';
import { skipToken } from '@tanstack/react-query';
import { StudentCourseEnrolment } from '@duke-hq/svc-api-db';
import { ResourceStatusBadge } from '@duke-hq/ui/components/ResourceStatusBadge/ResourceStatusBadge';
import { SectionedList } from '@duke-hq/ui/components/SectionedList/SectionedList';
import type { RouterOutputs } from '@duke-hq/svc-api/provider';
import type { CourseKind } from '@duke-hq/svc-api-db/schemas/enums/CourseKind.schema';

export function SelectSkillSet({
  goToNextStep,
  goToPrevStep,
  setStep,
}: {
  goToNextStep: () => void;
  goToPrevStep: () => void;
  setStep: (step: React.SetStateAction<number>) => void;
}) {
  const [search, setSearch] = useState<string>('');
  const [debouncedSearch] = useDebounce(search, 500);
  const snap = useSnapshot(internalEnrolmentState);
  const { selectedUserAttributes } = snap;

  const { data: coursesList, isLoading } = trpcTenant.courses.listWithMinimalAttributes.useQuery({
    search: debouncedSearch,
    fetchInternalEnrolments: true,
    order: 'category',
  });

  const { data: userEnrolments } = trpcTenant.enrolments.getByUserEmail.useQuery(
    snap.selectedUserAttributes.email ? { email: snap.selectedUserAttributes.email } : skipToken,
    { enabled: !!snap.selectedUserAttributes.id },
  );

  const handleInputChange = (e: ChangeEvent<HTMLInputElement>) => {
    const { value } = e.target;
    const q = value.trim();
    setSearch(q);
  };

  useEffect(() => {
    if (userEnrolments?.[0]?.user?.id != null) {
      internalEnrolmentState.setSelectedParticipant({
        id: userEnrolments[0].user.id,
      });
    }
  }, [userEnrolments]);

  if (!coursesList) {
    return (
      <Box w="full">
        <Text color="fg.muted" textAlign="left" textStyle="sm">
          <Trans>Sorry, we couldn't load courses. Please refresh or try again later.</Trans>
        </Text>
      </Box>
    );
  }

  return (
    <Stack spacing={4} w="full" h="full" justify="space-between">
      <FormMainContainer>
        <FormContentContainer>
          <ConsultantBanner
            firstName={selectedUserAttributes.first_name}
            lastName={selectedUserAttributes.last_name}
            email={selectedUserAttributes.email}
            role={selectedUserAttributes.role}
            canChangeConsultant={false}
            onChangeClick={() => setStep(0)}
          />
          <Input
            autoComplete="off"
            autoFocus
            fontSize="sm"
            minH="8"
            maxH="8"
            onChange={handleInputChange}
            placeholder="Search"
            size="sm"
            type="text"
            value={search}
            width="full"
          />
          {isLoading ? (
            <Center h="full" w="full">
              <Spinner color="border.accent" size="lg" />
            </Center>
          ) : (
            <CourseList courses={coursesList} userEnrolments={userEnrolments} />
          )}
        </FormContentContainer>
        <FormFooterContainer>
          <Button
            rightIcon={<ChevronRightIcon />}
            onClick={goToNextStep}
            isDisabled={!snap.selectedCourseAttributes.id}
          >
            <Trans>Next</Trans>
          </Button>
          <Button onClick={goToPrevStep} variant="tertiary" leftIcon={<ChevronLeftIcon />}>
            <Trans>Back</Trans>
          </Button>
        </FormFooterContainer>
      </FormMainContainer>
    </Stack>
  );
}

function CourseList({
  courses,
  userEnrolments,
}: {
  courses: RouterOutputs['courses']['listWithMinimalAttributes'] | undefined;
  userEnrolments: StudentCourseEnrolment[] | undefined;
}) {
  const snap = useSnapshot(internalEnrolmentState);

  const courseOptions = useMemo(() => {
    return (
      courses?.map((course) => {
        const enrolment = userEnrolments?.find(
          (e: StudentCourseEnrolment) => e.course_id === course.id,
        );
        return {
          course,
          enrolmentStatus: enrolment?.status,
          isDisabled: !!enrolment,
        };
      }) || []
    );
  }, [courses, userEnrolments]);

  return (
    <SectionedList
      items={courseOptions}
      getItemKey={(option) => option.course.id}
      getSection={(option) => courseSection(option.course)}
      isItemSelected={(option) => snap.selectedCourseAttributes.id === option.course.id}
      isItemDisabled={(option) => option.isDisabled}
      onSelectItem={(option) =>
        internalEnrolmentState.setSelectedCourseAttributes({
          id: option.course.id,
          identifier: option.course.identifier ?? '',
          title: option.course.title,
          specialisation: option.course.specialisation ?? '',
          price_aud: Number(option.course.price_aud),
          kind: option.course.kind as CourseKind,
        })
      }
      emptyState={
        <Text color="fg.muted" textAlign="left" textStyle="sm">
          <Trans>No courses found.</Trans>
        </Text>
      }
      renderItem={({ course, enrolmentStatus }) => (
        <Flex align="center" justify="space-between" w="full">
          <Box>
            <HStack justify="start">
              <Text color="fg.subtle" fontSize="sm" lineHeight="5">
                {course.identifier}
              </Text>
              {typeof course.specialisation === 'string' && course.specialisation !== '' ? (
                <Text color="fg.emphasis" fontSize="sm" lineHeight="5" fontWeight="bold">
                  {course.title}
                </Text>
              ) : null}
            </HStack>
            {typeof course.specialisation === 'string' && course.specialisation !== '' ? (
              <Text color="fg.subtle" fontSize="xs" lineHeight="5">
                {course.specialisation}
              </Text>
            ) : (
              <Text color="fg.emphasis" fontSize="sm" lineHeight="5" fontWeight="bold">
                <Box as="span">
                  {course.kind && CourseKindLabels[course.kind] != null ? (
                    <Box as="span">{CourseKindLabels[course.kind]}: </Box>
                  ) : null}
                  <Box as="span">{course.title}</Box>
                </Box>
              </Text>
            )}
          </Box>
          {enrolmentStatus ? (
            <Flex align="flex-end">
              <ResourceStatusBadge status={enrolmentStatus} />
            </Flex>
          ) : null}
        </Flex>
      )}
    />
  );
}
